import { Flex, Skeleton, Text } from '@backstage/ui';

import type { CheckRow } from '../../hooks/useProjectScorecard';
import { KpiCard } from '../KpiCard/KpiCard';
import { KpiRow } from '../KpiRow/KpiRow';
import { PassFailBar } from '../SegmentedBar/SegmentedBar';
import { RiMedalLine } from '@remixicon/react';
import { TrackFilters } from './TrackFilters';
import styles from './ProjectScorecards.module.css';
import { useLevels } from '../../hooks/useLevels';
import { useMemo } from 'react';

interface EntityKpiSectionProps {
  rows: CheckRow[];
  isLoading: boolean;
}

export const EntityKpiSection = ({
  rows,
  isLoading,
}: EntityKpiSectionProps) => {
  const { levels } = useLevels();
  const total = rows.length;
  const passing = rows.filter(r => r.snapshot?.result === 'passed').length;
  const pct = total > 0 ? Math.round((passing / total) * 100) : 0;

  const lastRunIso = rows
    .map(r => r.snapshot?.snapshotTime)
    .filter(Boolean)
    .sort()
    .at(-1);

  const failing = rows.filter(r => r.snapshot?.result === 'failed').length;

  const levelCounts = new Map<string, number>();
  const levelNames = new Map<string, string>();
  let othersCount = 0;
  for (const row of rows) {
    if (row.snapshot?.result === 'failed') {
      const levelId = row.check.levelId ?? null;
      if (levelId === null) {
        othersCount++;
      } else {
        levelCounts.set(levelId, (levelCounts.get(levelId) ?? 0) + 1);
        if (row.check.levelName) levelNames.set(levelId, row.check.levelName);
      }
    }
  }

  const trackStats = new Map<
    string,
    { name: string; passing: number; failing: number; total: number }
  >();
  for (const row of rows) {
    const trackId = row.check.trackId;
    const entry = trackStats.get(trackId) ?? {
      name: row.check.trackName,
      passing: 0,
      failing: 0,
      total: 0,
    };
    entry.total++;
    if (row.snapshot?.result === 'passed') entry.passing++;
    else if (row.snapshot?.result === 'failed') entry.failing++;
    trackStats.set(trackId, entry);
  }

  const orderedLevels = useMemo(
    () => [...levels].sort((a, b) => a.id.localeCompare(b.id)),
    [levels],
  );

  const projectLevel = useMemo(
    () =>
      orderedLevels.find(lv => {
        const atLevel = rows.filter(r => r.check.levelId === lv.id);
        return (
          atLevel.length > 0 &&
          atLevel.every(r => r.snapshot?.result === 'passed')
        );
      }) ?? null,
    [rows, orderedLevels],
  );

  if (isLoading) {
    return (
      <Flex
        gap="4"
        align="stretch"
        style={{ marginBottom: 'var(--bui-space-4)', flexWrap: 'wrap' }}
      >
        <Skeleton height={120} style={{ flex: '1 1 200px' }} />
        <Skeleton height={120} style={{ flex: '1 1 200px' }} />
      </Flex>
    );
  }

  if (total === 0) return null;

  return (
    <Flex
      direction="column"
      gap="4"
      style={{ marginBottom: 'var(--bui-space-4)' }}
    >
      <KpiRow columns={4}>
        <KpiCard
          label="Overall"
          colSpan={2}
          footer={
            lastRunIso ? (
              <Text as="p" variant="body-small" color="secondary">
                Last run: {new Date(lastRunIso).toLocaleString()}
              </Text>
            ) : undefined
          }
        >
          <Flex align="center" gap="4" className={styles.overallStats}>
            <Text as="span" variant="title-large">
              {pct}%
            </Text>
            <Text as="span" variant="body-small" color="secondary">
              {passing} / {total} passing
            </Text>
          </Flex>
          <PassFailBar passing={passing} failing={failing} total={total} />
        </KpiCard>
        <KpiCard label="Project Level" colSpan={1}>
          <Flex direction="column" align="center" justify="center" gap="2">
            <RiMedalLine
              size={64}
              style={{
                color: projectLevel
                  ? `var(--zf-sc-level-${projectLevel.id})`
                  : 'var(--bui-fg-secondary)',
              }}
            />
            <Text as="p" variant="body-small" color="secondary">
              {projectLevel
                ? `All ${projectLevel.name} checks passing`
                : 'No level fully achieved'}
            </Text>
          </Flex>
        </KpiCard>
        <KpiCard label="Failing Checks by Level" colSpan={1}>
          <Flex direction="column" gap="2">
            {orderedLevels.map(lv => {
              const count = levelCounts.get(lv.id) ?? 0;
              const name = levelNames.get(lv.id) ?? lv.name;
              return (
                <Flex key={lv.id} align="center" gap="2">
                  <Flex align="center" gap="2" grow={1}>
                    <RiMedalLine
                      size={24}
                      style={{
                        color: `var(--zf-sc-level-${lv.id})`,
                        flexShrink: 0,
                      }}
                    />
                    <Text as="span" variant="body-medium" color="primary">
                      {name}
                    </Text>
                  </Flex>
                  <Text as="span" variant="body-medium" color="primary">
                    {count}
                  </Text>
                </Flex>
              );
            })}
            {othersCount > 0 && (
              <Flex align="center" gap="2">
                <Flex align="center" gap="2" grow={1}>
                  <RiMedalLine
                    size={24}
                    style={{
                      color: 'var(--zf-sc-level-others)',
                      flexShrink: 0,
                    }}
                  />
                  <Text as="span" variant="body-medium" color="primary">
                    Others
                  </Text>
                </Flex>
                <Text as="span" variant="body-medium" color="primary">
                  {othersCount}
                </Text>
              </Flex>
            )}
          </Flex>
        </KpiCard>
      </KpiRow>
      <TrackFilters trackStats={trackStats} />
    </Flex>
  );
};
